import React from 'react';
import cn from 'classnames';

import { TextFormat } from 'widget/types/messages';
import type {
  MediaAttachment,
  DocumentAttachment
} from 'widget/types/attachments';
import EmojiText from 'widget/shared/ui/EmojiText';

import MessageDocument from '../MessageDocument';
import MessageMediaGrid from '../MessageMediaGrid';

import HtmlText from './HtmlText';
import styles from './MessageBody.module.scss';

export type MessageBodyProps = {
  text?: string;
  mediaAttachments: MediaAttachment[];
  documents: DocumentAttachment[];
  time: string;
  isFromBot?: boolean;
  isFirst?: boolean;
  isError?: boolean;
  textFormat?: TextFormat;
};

const MessageBody: React.FC<MessageBodyProps> = ({
  text,
  mediaAttachments,
  documents,
  time,
  isFromBot = false,
  isFirst = false,
  isError = false,
  textFormat
}) => {
  const hasMediaAttachments = mediaAttachments.length > 0;
  const hasDocuments = documents.length > 0;
  const documentVariant = isFromBot ? 'default' : ('user' as const);

  return (
    <div
      className={cn(
        styles['body'],
        isFromBot ? styles['body_from-bot'] : styles['body_from-user'],
        hasMediaAttachments && styles['body_has-media-attachments'],
        isFirst && styles['body_first'],
        isError && styles['body_error']
      )}
    >
      {hasMediaAttachments && (
        <MessageMediaGrid media={mediaAttachments} isFromBot={isFromBot} />
      )}

      {hasDocuments && (
        <div className={styles['body__documents']}>
          {documents.map((doc, index) => (
            <MessageDocument
              key={`${doc.url}-${index}`}
              url={doc.url}
              filename={doc.filename}
              size={doc.size}
              variant={documentVariant}
            />
          ))}
        </div>
      )}

      {text &&
        (textFormat === TextFormat.HTML ? (
          <HtmlText text={text} />
        ) : (
          <div className={styles['body__text']}>
            <EmojiText text={text} />
          </div>
        ))}

      <div
        className={cn(
          styles['body__time'],
          isFromBot && styles['body__time_from-bot']
        )}
      >
        {time}
      </div>
    </div>
  );
};

export default React.memo(MessageBody);
